<script setup lang="ts">
import type { DrillSet } from '@functions/types/drills';
import { doc } from 'firebase/firestore';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useRoute, useRouter } from 'vue-router';
import { useDocument, useFirestore } from 'vuefire';
import DrillCard from '@/components/reports/DrillCard.vue';
import { useDebugUser } from '@/composables/useDebugUser';
import Container from '@/layouts/Container.vue';

definePage({
  name: 'drill-set',
});

const route = useRoute();
const router = useRouter();
const user = useDebugUser();
const toast = useToast();
const db = useFirestore();

interface RouteParams {
  drillSetId: string;
}

const drillSetId = (route.params as RouteParams).drillSetId;
const drillSetRef = doc(db, `drills/${user.value?.uid}/sets/${drillSetId}`);
const { data: drillSet } = useDocument<DrillSet>(drillSetRef);

// Navigate to drill progress page
function viewProgress() {
  router.push(`/drills/${drillSetId}/progress`);
}

// Share drill set
function shareDrillSet() {
  if (!drillSet.value) return;

  // Create shareable URL
  const shareUrl = `${window.location.origin}/drills/${drillSetId}`;

  // Use Web Share API if available
  if (navigator.share) {
    navigator.share({
      title: 'SHOTMETRICS AI Practice Plan',
      text: 'Check out this golf practice plan I found on SHOTMETRICS AI',
      url: shareUrl,
    }).catch((err) => {
      console.error('Error sharing:', err);
      fallbackShare(shareUrl);
    });
  }
  else {
    fallbackShare(shareUrl);
  }
}

// Fallback sharing method (copy to clipboard)
function fallbackShare(url: string) {
  navigator.clipboard.writeText(url).then(() => {
    toast.add({
      severity: 'success',
      summary: 'Link Copied',
      detail: 'Shareable link copied to clipboard',
      life: 3000,
    });
  }).catch((err) => {
    console.error('Could not copy text: ', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to copy link to clipboard',
      life: 3000,
    });
  });
}
</script>

<template>
  <Toast />
  <Container>
    <template #title>
      Practice Plan
    </template>

    <div class="mb-4 flex items-center gap-2">
      <router-link to="/drills" class="hover:text-gray-600 dark:hover:text-gray-400 flex items-center gap-1 text-black dark:text-white">
        <i class="pi pi-arrow-left" />
        Back to All Plans
      </router-link>
    </div>

    <div v-if="drillSet">
      <div class="mb-6 flex md:flex-row flex-col-reverse gap-4 md:gap-0 justify-between items-start">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
            Practice Plan
          </h2>
          <p class="mt-2 text-gray-600 dark:text-gray-400">
            These drills have been personalized based on your recent performance data.<br>
            Upload one session per drill to see your progress!
          </p>
        </div>

        <div class="flex gap-2">
          <button
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
            @click="shareDrillSet"
          >
            <i class="pi pi-share-alt" />
            Share
          </button>

          <button
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
            @click="viewProgress"
          >
            <i class="pi pi-chart-line" />
            Track Progress
          </button>
        </div>
      </div>

      <div class="space-y-6">
        <DrillCard v-for="(drill, index) in drillSet.drills" :key="index" :drill="drill" />
      </div>

      <div class="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <h3 class="text-lg font-medium text-blue-900">
          Track Your Progress
        </h3>
        <p class="mt-2 text-blue-700">
          After practicing these drills, upload a new session to see how you've improved!
        </p>
        <div class="mt-4 flex gap-3">
          <router-link
            :to="`/upload?drillSetId=${drillSetId}`"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <i class="pi pi-upload mr-2" />
            Upload Practice Session
          </router-link>

          <button
            class="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            @click="viewProgress"
          >
            <i class="pi pi-chart-line mr-2" />
            View Progress
          </button>
        </div>
      </div>
    </div>

    <div v-else class="flex min-h-[60vh] items-center justify-center flex-col gap-2">
      <div class="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      <p class="text-gray-900 dark:text-gray-200">
        Loading drills...
      </p>
    </div>
  </Container>
</template>
